import { motion, type HTMLMotionProps } from "framer-motion";
import { useMemo } from "react";
import { useMotionPrefs } from "./MotionProvider";
import { fade, type FadeOptions } from "./variants";

type Tag = "div" | "section" | "article" | "li" | "figure" | "header" | "span" | "p";

type RevealProps = FadeOptions &
  Omit<HTMLMotionProps<"div">, "variants" | "initial" | "whileInView" | "viewport"> & {
    as?: Tag;
    /**
     * Must be false inside <Stagger>. Declaring initial/whileInView on a child
     * makes it its own variant controller, and it stops inheriting the parent's
     * stagger delay entirely — the stagger silently disappears.
     */
    standalone?: boolean;
  };

export function Reveal({
  as = "div",
  dir,
  distance,
  duration,
  delay,
  standalone = true,
  children,
  ...rest
}: RevealProps) {
  const { reduce, viewport } = useMotionPrefs();
  const Tag = motion[as] as typeof motion.div;

  const variants = useMemo(
    () => fade({ dir, distance, duration, delay }, reduce),
    [dir, distance, duration, delay, reduce],
  );

  return (
    <Tag
      data-reveal=""
      variants={variants}
      {...(standalone
        ? { initial: "hidden" as const, whileInView: "visible" as const, viewport }
        : null)}
      {...rest}
    >
      {children}
    </Tag>
  );
}
